import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Search, Pencil, Trash2, Mail, ShieldOff, ShieldCheck, Eye, EyeOff } from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import api from '@/lib/api';
import { ConfirmDialog } from '@/components/ConfirmDialog';

const ROLE_PILL = 'inline-flex rounded-full px-2 py-0.5 text-xs font-medium capitalize ring-1 ring-inset';

function UserGroupTable({ title, subtitle, rows, showSponsors, sponsorsLabel = 'Sponsors', roleBadge, isLocked, onEdit, onUnlock, testId }) {
  return (
    <div data-testid={testId}>
      <div className="mb-3">
        <div className="flex items-baseline gap-2">
          <h2 className="section-title">{title}</h2>
          <span className="text-xs text-gray-400">{rows.length}</span>
        </div>
        <p className="text-xs text-gray-400 mt-0.5">{subtitle}</p>
      </div>
      <Card className="bg-white dark:bg-gray-900 border-0 rounded-card shadow-card">
        <CardContent className="p-0">
          {rows.length === 0 ? (
            <p className="text-center py-10 text-sm text-gray-400">No users in this group</p>
          ) : (
            <Table className="table-fixed w-full">
              <TableHeader>
                <TableRow className="border-gray-100 dark:border-gray-800 hover:bg-transparent">
                  <TableHead className="text-xs text-gray-400 font-medium text-start w-[30%]">Member</TableHead>
                  <TableHead className="text-xs text-gray-400 font-medium text-start w-[18%]">Role</TableHead>
                  <TableHead className="text-xs text-gray-400 font-medium text-start hidden sm:table-cell w-[14%]">Status</TableHead>
                  {showSponsors && <TableHead className="text-xs text-gray-400 font-medium text-start hidden md:table-cell">{sponsorsLabel}</TableHead>}
                  <TableHead className="text-xs text-gray-400 font-medium text-end w-20"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map(u => (
                  <TableRow key={u.id} data-testid={`user-row-${u.id}`} className="border-gray-100 dark:border-gray-800 hover:bg-gray-50/80 dark:hover:bg-gray-800/40 transition-colors">
                    <TableCell>
                      <div className="flex items-center gap-2.5 min-w-0">
                        {u.avatar_url ? (
                          <img src={u.avatar_url} alt="" loading="lazy" className="size-7 shrink-0 rounded-full object-cover outline outline-1 -outline-offset-1 outline-black/10 dark:outline-white/10" />
                        ) : (
                          <div className="size-7 shrink-0 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center text-[11px] font-semibold text-gray-500 outline outline-1 -outline-offset-1 outline-black/10 dark:outline-white/10">
                            {u.name?.[0]}
                          </div>
                        )}
                        <span className="min-w-0">
                          <span className="block truncate text-sm font-medium text-gray-900 dark:text-gray-100">{u.name}</span>
                          <span className="block truncate text-xs font-normal text-gray-500 dark:text-gray-400">{u.email}</span>
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className={`${ROLE_PILL} ${roleBadge[u.role] || 'bg-gray-100 text-gray-600 ring-gray-500/15 dark:bg-gray-100/10 dark:text-gray-300 dark:ring-white/15'}`}>
                        {u.role?.replace(/_/g, ' ')}
                      </span>
                    </TableCell>
                    <TableCell className="hidden sm:table-cell">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        {u.status === 'active' ? (
                          <span className="inline-flex items-center gap-1.5 text-xs text-emerald-700 dark:text-emerald-300">
                            <ShieldCheck size={14} /> Active
                          </span>
                        ) : (
                          <span className="text-xs text-gray-400 dark:text-gray-500">Inactive</span>
                        )}
                        {u.is_archived && (
                          <span data-testid={`archived-badge-${u.id}`} className="inline-flex items-center gap-1 text-[10px] font-medium text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-800 ring-1 ring-inset ring-gray-400/20 rounded-full px-1.5 py-0.5">
                            Archived
                          </span>
                        )}
                        {isLocked(u) && (
                          <span data-testid={`locked-badge-${u.id}`} className="inline-flex items-center gap-1 text-[10px] font-medium text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-950/40 ring-1 ring-inset ring-red-500/20 rounded-full px-1.5 py-0.5">
                            Locked
                          </span>
                        )}
                      </div>
                    </TableCell>
                    {showSponsors && (
                      <TableCell className="hidden md:table-cell">
                        {u.sponsor_names?.length ? (
                          <div className="flex flex-wrap gap-1">
                            {u.sponsor_names.map((name, idx) => (
                              <span key={idx} data-testid={`sponsor-label-${u.id}-${idx}`} className="text-[10px] font-normal bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300 ring-1 ring-inset ring-amber-500/20 rounded px-1.5 py-0.5">
                                {name}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="text-xs text-gray-300 dark:text-gray-600" title={u.role === 'admin' ? 'Admins see every sponsor' : 'Nothing assigned yet'}>
                            {u.role === 'admin' ? 'All sponsors' : '—'}
                          </span>
                        )}
                      </TableCell>
                    )}
                    <TableCell className="text-end">
                      <div className="flex items-center justify-end gap-1">
                        {isLocked(u) && (
                          <Button data-testid={`unlock-user-${u.id}`} variant="ghost" size="sm" title="Unlock account"
                            className="h-7 w-7 p-0 text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950"
                            onClick={() => onUnlock(u.id, u.name)}>
                            <ShieldOff size={14} />
                          </Button>
                        )}
                        <Button variant="ghost" size="sm" data-testid={`edit-user-${u.id}`} title="Edit user"
                          className="h-7 w-7 p-0 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                          onClick={() => onEdit(u)}>
                          <Pencil size={14} />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default function UsersPage() {
  const { t } = useTranslation();
  const [users, setUsers] = useState([]);
  const [sponsors, setSponsors] = useState([]);
  const [roles, setRoles] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', role: 'account_manager', sponsor_id: '', send_invite: true });
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [showArchived, setShowArchived] = useState(false);

  const load = async (withArchived = showArchived) => {
    try {
      const [u, s, r] = await Promise.all([
        api.get(`/api/users${withArchived ? '?include_archived=true' : ''}`),
        api.get('/api/sponsors'),
        api.get('/api/roles'),
      ]);
      setUsers(u.data);
      setSponsors(s.data.filter(sp => sp.status === 'active'));
      setRoles(r.data);
    } catch { toast.error('Failed to load'); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const toggleArchived = () => {
    const next = !showArchived;
    setShowArchived(next);
    load(next);
  };
  const archivedCount = users.filter(u => u.is_archived).length;

  const filtered = users.filter(u =>
    u.name?.toLowerCase().includes(search.toLowerCase()) ||
    u.email?.toLowerCase().includes(search.toLowerCase()) ||
    u.role?.toLowerCase().includes(search.toLowerCase())
  );

  // Sponsor-side users go in their own table; everyone else is internal, including
  // users on custom roles (they used to match neither list and vanished from the page).
  const internalUsers = filtered.filter(u => u.role !== 'sponsor');
  const sponsorUsers = filtered.filter(u => u.role === 'sponsor');

  const handleCreate = async () => {
    if (!form.name || !form.email) { toast.error(t('fill_required')); return; }
    if (form.role === 'sponsor' && !form.sponsor_id) { toast.error('Please select a sponsor'); return; }
    if (form.role === 'spo_viewer' && (!form.sponsor_ids || form.sponsor_ids.length === 0)) { toast.error('Please select at least one sponsor'); return; }
    setSaving(true);
    try {
      const res = await api.post('/api/users', form);
      toast.success(`User created. ${form.send_invite ? 'Invitation email sent.' : `Temp password: ${res.data.temp_password}`}`);
      setCreateOpen(false);
      setForm({ name: '', email: '', role: 'account_manager', sponsor_id: '', sponsor_ids: [], send_invite: true });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  const handleEdit = (u) => { setEditForm({ ...u, new_password: '' }); setEditOpen(true); };

  const handleSaveEdit = async () => {
    if (!editForm) return;
    setSaving(true);
    try {
      await api.put(`/api/users/${editForm.id}`, {
        name: editForm.name, email: editForm.email, role: editForm.role,
        status: editForm.status, sponsor_id: editForm.sponsor_id || null,
        sponsor_ids: editForm.sponsor_ids || [],
      });
      if (editForm.new_password && editForm.new_password.length > 0) {
        if (editForm.new_password.length < 6) { toast.error('Password must be at least 6 characters'); setSaving(false); return; }
        await api.post(`/api/users/${editForm.id}/change-password`, { new_password: editForm.new_password });
      }
      toast.success('User updated');
      setEditOpen(false);
      setEditForm(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!editForm) return;
    setSaving(true);
    try {
      await api.delete(`/api/users/${editForm.id}`);
      toast.success('User deleted');
      setEditOpen(false);
      setEditForm(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); setConfirmOpen(false); }
  };

  const roleBadge = {
    admin: 'bg-neutral-900 text-white ring-neutral-900/20 dark:bg-white/10 dark:text-neutral-100 dark:ring-white/20',
    account_manager: 'bg-blue-50 text-blue-700 ring-blue-600/20 dark:bg-blue-400/10 dark:text-blue-300 dark:ring-blue-300/25',
    supervisor: 'bg-indigo-50 text-indigo-700 ring-indigo-600/20 dark:bg-indigo-400/10 dark:text-indigo-300 dark:ring-indigo-300/25',
    sponsor: 'bg-emerald-50 text-emerald-700 ring-emerald-600/20 dark:bg-emerald-400/10 dark:text-emerald-300 dark:ring-emerald-300/25',
    spo_viewer: 'bg-violet-50 text-violet-700 ring-violet-600/20 dark:bg-violet-400/10 dark:text-violet-300 dark:ring-violet-300/25',
  };

  const isLocked = (u) => {
    if (!u.locked_until) return false;
    return new Date(u.locked_until) > new Date();
  };

  const handleUnlock = async (userId, userName) => {
    try {
      await api.post(`/api/auth/unlock/${userId}`);
      toast.success(`${userName}'s account has been unlocked`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to unlock'); }
  };

  return (
    <div data-testid="users-page" className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">{t('users_management') || 'Users'}</h1>
          <p className="text-gray-400 mt-1 text-sm">{users.length} {t('users').toLowerCase()}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            data-testid="toggle-archived-users-btn"
            variant="outline"
            onClick={toggleArchived}
            className="text-sm h-9"
          >
            {showArchived ? <EyeOff size={15} className="me-1.5" /> : <Eye size={15} className="me-1.5" />}
            {showArchived ? t('hide_archived', 'Hide archived') : t('show_archived', 'Show archived')}
            {showArchived && archivedCount > 0 && (
              <span className="ms-1.5 tabular-nums text-xs text-gray-400">({archivedCount})</span>
            )}
          </Button>
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogTrigger asChild>
            <Button data-testid="invite-user-btn" className="bg-gray-900 text-white hover:bg-gray-800 text-sm h-9">
              <Plus size={15} className="mr-1.5" /> {t('invite_user') || 'Invite User'}
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-white border-gray-200 max-w-md">
            <DialogHeader><DialogTitle className="text-lg font-semibold text-gray-900">{t('invite_user') || 'Invite User'}</DialogTitle></DialogHeader>
            <div className="space-y-4 mt-4">
              <div><Label className="text-sm text-gray-600">{t('name')} *</Label>
                <Input data-testid="user-name" value={form.name} onChange={e => setForm(p => ({...p, name: e.target.value}))} className="bg-gray-50 border-gray-200 h-10 text-sm mt-1" /></div>
              <div><Label className="text-sm text-gray-600">{t('email')} *</Label>
                <Input data-testid="user-email" type="email" value={form.email} onChange={e => setForm(p => ({...p, email: e.target.value}))} className="bg-gray-50 border-gray-200 h-10 text-sm mt-1" /></div>
              <div><Label className="text-sm text-gray-600">{t('role')}</Label>
                <Select value={form.role} onValueChange={v => setForm(p => ({...p, role: v, sponsor_id: v === 'sponsor' ? p.sponsor_id : '', sponsor_ids: v === 'spo_viewer' ? (p.sponsor_ids || []) : []}))}>
                  <SelectTrigger className="bg-gray-50 border-gray-200 h-10 text-sm mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white border-gray-200">
                    {roles.map(r => (
                      <SelectItem key={r.key} value={r.key}>{r.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select></div>
              {form.role === 'sponsor' && (
                <div><Label className="text-sm text-gray-600">{t('sponsor')} *</Label>
                  <Select value={form.sponsor_id} onValueChange={v => setForm(p => ({...p, sponsor_id: v}))}>
                    <SelectTrigger className="bg-gray-50 border-gray-200 h-10 text-sm mt-1"><SelectValue placeholder={t('select_sponsor')} /></SelectTrigger>
                    <SelectContent className="bg-white border-gray-200">
                      {sponsors.map(s => <SelectItem key={s.id} value={s.id}>{s.company_name}</SelectItem>)}
                    </SelectContent>
                  </Select></div>
              )}
              {form.role === 'spo_viewer' && (
                <div><Label className="text-sm text-gray-600">{t('sponsors') || 'Sponsors'} *</Label>
                  <div className="mt-1 space-y-1 max-h-40 overflow-y-auto border border-gray-200 rounded-md p-2 bg-gray-50">
                    {sponsors.map(s => (
                      <label key={s.id} className="flex items-center gap-2 text-sm cursor-pointer py-1 px-1 hover:bg-gray-100 rounded">
                        <input type="checkbox" checked={(form.sponsor_ids || []).includes(s.id)}
                          onChange={e => {
                            const ids = form.sponsor_ids || [];
                            setForm(p => ({...p, sponsor_ids: e.target.checked ? [...ids, s.id] : ids.filter(id => id !== s.id)}));
                          }}
                          className="rounded" />
                        {s.company_name}
                      </label>
                    ))}
                  </div></div>
              )}
              <label className="flex items-center gap-2 text-sm text-gray-500 cursor-pointer">
                <input type="checkbox" checked={form.send_invite} onChange={e => setForm(p => ({...p, send_invite: e.target.checked}))} className="rounded" />
                <Mail size={14} /> {t('send_invite_email') || 'Send invitation email'}
              </label>
              <Button data-testid="save-user-btn" onClick={handleCreate} disabled={saving} className="w-full bg-gray-900 text-white hover:bg-gray-800 text-sm h-10">
                {saving ? '...' : (t('invite_user') || 'Invite User')}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <div className="relative max-w-sm">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <Input data-testid="user-search" placeholder={t('search') || 'Search users...'} value={search} onChange={e => setSearch(e.target.value)} className="pl-9 bg-white border-gray-200 h-9 text-sm" />
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-400 text-sm">{t('loading')}</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 text-gray-400 text-sm">No users found</div>
      ) : (
        <div className="space-y-8">
          <UserGroupTable
            title="Internal Team"
            subtitle="Admins, account managers, supervisors, SPO viewers and custom roles"
            rows={internalUsers}
            showSponsors
            sponsorsLabel="Manages"
            roleBadge={roleBadge} isLocked={isLocked}
            onEdit={handleEdit} onUnlock={handleUnlock}
            testId="internal-users-table"
          />
          <UserGroupTable
            title="Sponsor Users"
            subtitle="Sponsor contacts who raise and approve their own requests"
            rows={sponsorUsers}
            showSponsors
            roleBadge={roleBadge} isLocked={isLocked}
            onEdit={handleEdit} onUnlock={handleUnlock}
            testId="sponsor-users-table"
          />
        </div>
      )}

      <Dialog open={editOpen} onOpenChange={o => { if (!o) { setEditOpen(false); setEditForm(null); } else setEditOpen(true); }}>
        <DialogContent className="bg-white border-gray-200 max-w-md">
          <DialogHeader><DialogTitle className="text-lg font-semibold text-gray-900">{t('edit')} {t('user') || 'User'}</DialogTitle></DialogHeader>
          {editForm && (
            <div className="space-y-4 mt-4">
              <div><Label className="text-sm text-gray-600">{t('name')} <span className="text-red-500">*</span></Label>
                <Input value={editForm.name || ''} onChange={e => setEditForm(p => ({...p, name: e.target.value}))} className="bg-gray-50 border-gray-200 h-10 text-sm mt-1" /></div>
              <div><Label className="text-sm text-gray-600">{t('email')} <span className="text-red-500">*</span></Label>
                <Input type="email" value={editForm.email || ''} onChange={e => setEditForm(p => ({...p, email: e.target.value}))} className="bg-gray-50 border-gray-200 h-10 text-sm mt-1" /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label className="text-sm text-gray-600">{t('role')}</Label>
                  <Select value={editForm.role} onValueChange={v => setEditForm(p => ({...p, role: v, sponsor_id: v === 'sponsor' ? p.sponsor_id : '', sponsor_ids: v === 'spo_viewer' ? (p.sponsor_ids || []) : []}))}>
                    <SelectTrigger className="bg-gray-50 border-gray-200 h-10 text-sm mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white border-gray-200">
                      {roles.map(r => (
                        <SelectItem key={r.key} value={r.key}>{r.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select></div>
                <div><Label className="text-sm text-gray-600">{t('status')}</Label>
                  <Select value={editForm.status} onValueChange={v => setEditForm(p => ({...p, status: v}))}>
                    <SelectTrigger className="bg-gray-50 border-gray-200 h-10 text-sm mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white border-gray-200">
                      <SelectItem value="active">{t('active')}</SelectItem>
                      <SelectItem value="inactive">{t('inactive')}</SelectItem>
                    </SelectContent>
                  </Select></div>
              </div>
              {editForm.role === 'sponsor' && (
                <div><Label className="text-sm text-gray-600">{t('sponsor')} *</Label>
                  <Select value={editForm.sponsor_id || ''} onValueChange={v => setEditForm(p => ({...p, sponsor_id: v}))}>
                    <SelectTrigger data-testid="edit-user-sponsor" className="bg-gray-50 border-gray-200 h-10 text-sm mt-1"><SelectValue placeholder={t('select_sponsor')} /></SelectTrigger>
                    <SelectContent className="bg-white border-gray-200">
                      {sponsors.map(s => <SelectItem key={s.id} value={s.id}>{s.company_name}</SelectItem>)}
                    </SelectContent>
                  </Select></div>
              )}
              {editForm.role === 'spo_viewer' && (
                <div><Label className="text-sm text-gray-600">{t('sponsors') || 'Sponsors'} *</Label>
                  <div className="mt-1 space-y-1 max-h-40 overflow-y-auto border border-gray-200 rounded-md p-2 bg-gray-50">
                    {sponsors.map(s => (
                      <label key={s.id} className="flex items-center gap-2 text-sm cursor-pointer py-1 px-1 hover:bg-gray-100 rounded">
                        <input type="checkbox" checked={(editForm.sponsor_ids || []).includes(s.id)}
                          onChange={e => {
                            const ids = editForm.sponsor_ids || [];
                            setEditForm(p => ({...p, sponsor_ids: e.target.checked ? [...ids, s.id] : ids.filter(id => id !== s.id)}));
                          }}
                          className="rounded" />
                        {s.company_name}
                      </label>
                    ))}
                  </div></div>
              )}
              <div>
                <Label className="text-sm text-gray-600">Change Password</Label>
                <Input data-testid="edit-user-password" type="text" value={editForm.new_password || ''} onChange={e => setEditForm(p => ({...p, new_password: e.target.value}))} placeholder="Leave empty to keep current" className="bg-gray-50 border-gray-200 h-10 text-sm mt-1" />
              </div>
              <DialogFooter className="flex gap-2 pt-2">
                <Button variant="outline" onClick={() => setConfirmOpen(true)} disabled={saving} className="border-red-300 text-red-600 hover:bg-red-50">
                  <Trash2 size={13} className="mr-1" /> {t('delete')}
                </Button>
                <div className="flex-1" />
                <Button variant="outline" onClick={() => { setEditOpen(false); setEditForm(null); }} className="border-gray-200">{t('cancel')}</Button>
                <Button onClick={handleSaveEdit} disabled={saving} className="bg-gray-900 text-white hover:bg-gray-800 text-sm">
                  {saving ? '...' : (t('save_changes'))}
                </Button>
              </DialogFooter>
              <ConfirmDialog open={confirmOpen} onCancel={() => setConfirmOpen(false)} onConfirm={handleDelete}
                title="Delete User" description={`Are you sure you want to delete ${editForm?.name}?`} confirmText="Delete" destructive />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
